import { Injectable } from "@angular/core";
import { KeycloakHelperService } from "./keycloak.helper.service";
import { IConfig, initialConfig } from "ngx-mask";

interface MaskTypes {
  postalCodeMask: string;
  taxIdentifierMask: string;
  vtvMask: string;
  chassiMask: string;
  onlyNumbersMask: string;
  licensePlateMask: string;
  costCenterMask: string;
  yearMask: string;
  phoneNumberMask: string;
  thousandSeparator: string;
  decimalSeparator: string;
  currencyMask: string;
  customPatterns: IConfig["patterns"];
  timeMinutes: string;
}

interface ValidatorsType {
  taxIdentifierMinLength: number;
  licensePlateMinLength: number;
}

const brazilianMasks: MaskTypes = {
  postalCodeMask: "00000-000",
  taxIdentifierMask: "00.000.000/0000-00",
  vtvMask: "00/0000",
  chassiMask: "A{17}",
  onlyNumbersMask: "0*",
  licensePlateMask: "SSS-0A00",
  timeMinutes: "00:00",
  costCenterMask: "A{15}",
  yearMask: "0000",
  phoneNumberMask: "(00) 00000-0000||(00) 0000-0000",
  thousandSeparator: ".",
  decimalSeparator: ",",
  currencyMask: "R$ #,##0.00",
  customPatterns: {
    ...initialConfig.patterns
  }
};

const brazilianValidators: ValidatorsType = {
  taxIdentifierMinLength: 14,
  licensePlateMinLength: 8
};

const argentinianMasks: MaskTypes = {
  postalCodeMask: "S0000SSS",
  taxIdentifierMask: "00-00000000-0",
  vtvMask: "00/0000",
  chassiMask: "A{17}",
  onlyNumbersMask: "0*",
  timeMinutes: "00:00",
  licensePlateMask: "AAAAAAA",
  costCenterMask: "A{15}",
  yearMask: "0000",
  phoneNumberMask: "00000000000",
  thousandSeparator: ".",
  decimalSeparator: ",",
  currencyMask: "AR$ #,##0.00",
  customPatterns: {
    ...initialConfig.patterns
  }
};

const argentinianValidators: ValidatorsType = {
  taxIdentifierMinLength: 11,
  licensePlateMinLength: 6
};

const northAmericanMasks: MaskTypes = {
  postalCodeMask: "00000",
  taxIdentifierMask: "000000000",
  vtvMask: "00/0000",
  chassiMask: "A{17}",
  onlyNumbersMask: "0*",
  timeMinutes: "00:00",
  licensePlateMask: "P{10}",
  costCenterMask: "A{15}",
  yearMask: "0000",
  phoneNumberMask: "(000) 000-0000",
  thousandSeparator: ",",
  decimalSeparator: ".",
  currencyMask: "$ #,##0.00",
  customPatterns: {
    ...initialConfig.patterns,
    P: { pattern: new RegExp("[a-zA-Z0-9-]") }
  }
};

const northAmericanValidators: ValidatorsType = {
  taxIdentifierMinLength: 9,
  licensePlateMinLength: 1
};

const nederlandMasks: MaskTypes = {
  postalCodeMask: "0000AA",
  taxIdentifierMask: "00.000.000/0000-00",
  vtvMask: "00/0000",
  chassiMask: "A{17}",
  onlyNumbersMask: "0*",
  timeMinutes: "00:00",
  licensePlateMask: "A{8}",
  costCenterMask: "A{15}",
  yearMask: "0000",
  phoneNumberMask: "(00) 000-0000",
  thousandSeparator: ",",
  decimalSeparator: ".",
  currencyMask: "EUR #,##0.00",
  customPatterns: {
    ...initialConfig.patterns
  }
};

const nederlandValidators: ValidatorsType = {
  taxIdentifierMinLength: 9,
  licensePlateMinLength: 8
};

/**
 * The stantard mask lib should be ngx-mask.
 * {@link https://www.npmjs.com/package/ngx-mask}
 */
@Injectable({
  providedIn: "root"
})
export class MaskService {
  masks: MaskTypes = brazilianMasks;
  validators: ValidatorsType = brazilianValidators;

  constructor(private keycloakHelperService: KeycloakHelperService) {
    if (this.keycloakHelperService.isArgentinian()) {
      this.masks = argentinianMasks;
      this.validators = argentinianValidators;
    }

    if (this.keycloakHelperService.isNorthAmerican()) {
      this.masks = northAmericanMasks;
      this.validators = northAmericanValidators;
    }

    if (this.keycloakHelperService.isNederland()) {
      this.masks = nederlandMasks;
      this.validators = nederlandValidators;
    }
  }

  getVtvMask(): string {
    return this.masks.vtvMask;
  }

  getOnlyNumbersMask(): string {
    return this.masks.onlyNumbersMask;
  }

  getChassiMask(): string {
    return this.masks.chassiMask;
  }

  getLicensePlateMask(): string {
    return this.masks.licensePlateMask;
  }

  getCostCenterMask(): string {
    return this.masks.costCenterMask;
  }

  getPostalCodeMask(): string {
    return this.masks.postalCodeMask;
  }

  getYearMask(): string {
    return this.masks.yearMask;
  }

  getPhoneMask(): string {
    return this.masks.phoneNumberMask;
  }

  getThousandSeparator(): string {
    return this.masks.thousandSeparator;
  }

  getDecimalSeparator() {
    return this.masks.decimalSeparator;
  }

  getTaxIdentifierMask() {
    return this.masks.taxIdentifierMask;
  }

  getCustomPatterns() {
    return this.masks.customPatterns;
  }

  getValidators() {
    return this.validators;
  }

  applyPatternLicencePlate(value: string) {
    const brazilianPattern =
      /^([^0-9][^0-9][^0-9]\-[0-9][A-Za-z0-9][0-9][0-9])/;
    const northAmericanPattern = /^([a-zA-Z0-9-]{1,10})$/;

    let errors;

    if (this.keycloakHelperService.isBrazilian()) {
      errors = !brazilianPattern.test(value);
      return { errors };
    }

    if (this.keycloakHelperService.isNorthAmerican()) {
      errors = !northAmericanPattern.test(value);
      return { errors };
    }

    if (this.keycloakHelperService.isNederland()) {
      errors = !/[a-zA-Z-0-9-]{8}/.test(value);
      return { errors };
    }

    errors =
      value.length <= 6
        ? !/^[a-zA-Z]{3}[0-9]{3}/.test(value)
        : !/^[a-zA-Z]{2}[0-9]{3}[a-zA-Z]{2}/.test(value);

    return { errors };
  }

  getTimeMinutesMask() {
    return this.masks.timeMinutes;
  }
}
